import { Component, OnInit } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { FormHelper } from 'projects/admin/src/app/shared/form-helper';
import { AbstractControl, UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { RoleModel } from 'projects/core/src/lib/models/role.model';

@Component({
  selector: 'app-rights',
  templateUrl: './rights.component.html',
  styleUrls: ['./rights.component.scss']
})
export class RightsComponent implements OnInit {

  public loading = true;
  public submitting = false;
  public messages: Array<any> = [];
  public inEdit = false;
  public tabIndex = 0;
  public rights: any = null;
  public dependencies: any = null;
  public roles: any = null;

  public form = this.fb.group({});

  public search: string = '';
  public custom: boolean = false;

  private baseRightsData: any = null;

  constructor(
    private api: ApiService,
    private fb: UntypedFormBuilder,
    private ui: UiService,
    private transloco: TranslocoService
  ) { }

  ngOnInit(): void {
    this.api.getRights().subscribe({
      next: (result: any) => {
        this.setRoles(result.data.roles);
        this.setRights(result.data.rights);
        this.dependencies = result.data.dependencies;
        this.baseRightsData = result.data.rights;
      }
    }).add(() => {
      this.loading = false;
    });
  }

  onRightClicked(right: any, role: RoleModel, subGroup: any, control: AbstractControl): void {
    if (this.inEdit === true) {
      const neighbors: {[key: string]: AbstractControl} = this.getNeighborControls(right, role, subGroup.rights);
      const rightName: string = this.getRightBasename(right.name);
      if (control.value === true) {
        // checked
        if (this.dependencies[rightName]) {
          this.dependencies[rightName].forEach((dependency: string) => {
            if (neighbors[dependency]) {
              neighbors[dependency].setValue(true);
            }
          });
        }
      } else {
        // unchecked
        Object.keys(this.dependencies).forEach((key: string) => {
          if (this.dependencies[key].indexOf(rightName) !== -1) {
            neighbors[key].setValue(false);
          }
        });
      }
    }
  }

  private getNeighborControls(right: any, role: RoleModel, rights: Array<any>): {[key: string]: AbstractControl} {
    const neighbors: {[key: string]: AbstractControl} = {};
    rights.forEach((actual: any) => {
      Object.keys(actual.controls).forEach((key: string) => {
        if (key === role.name && actual.name !== right.name) {
          neighbors[this.getRightBasename(actual.name)] = actual.controls[key];
        }
      });
    });
    return neighbors;
  }

  private getRightBasename(right: any): string {
    const regex = /([a-z]+)$/g;
    const found: Array<string> = right.match(regex);
    return (found.length > 0) ? found[0] : right;
  }

  onEdit(): void {
    this.form.enable();
    this.inEdit = true;
  }

  onCancel(): void {
    this.form.disable();
    this.inEdit = false;
    this.setRights(this.baseRightsData);
  }

  onSubmit(): void {
    if (this.form.valid === true) {
      this.submitting = true;
      const payload: any = {};
      this.roles.forEach((role: RoleModel) => {
        payload[role.name] = [];
      });
      this.rights.forEach((mainGroup: any) => {
        mainGroup.subGroups.forEach((subGroup: any) => {
          subGroup.rights.forEach((right: any) => {
            Object.keys(right.controls).forEach((key: string) => {
              if (right.controls[key].value === true) {
                payload[key].push(right.name);
              }
            });
          });
        });
      });
      this.api.saveRights(payload).subscribe({
        next: (result: any) => {
          this.setRoles(result.data.roles);
          this.setRights(result.data.rights);
          this.inEdit = false;
        }
      }).add(() => {
        this.form.disable();
        this.submitting = false;
      });
    } else {
      this.ui.showMessage('error', this.transloco.translate('common.error_on_form_please_correct'), this.transloco.translate('common.error'));
      FormHelper.markFormAsDirty(this.form);
    }
  }

  public isHidden(role: RoleModel): boolean {
    if (this.custom === true && role.editable === false) {
      return true;
    }
    if (this.search.length > 0) {
      return !role.name.toLowerCase().includes(this.search.toLowerCase());
    }
    return false;
  }

  protected setRights(rights: any): void {
    this.form = this.fb.group({});
    this.rights = [];
    Object.keys(rights).forEach((lev1: string) => {
      const mainGroup: any = {
        name: lev1,
        subGroups: []
      }
      Object.keys(rights[lev1]).forEach((lev2: string) => {
        const subGroup: any = {
          name: lev2,
          rights: []
        }
        const list: Array<string> = rights[lev1][lev2];
        list.forEach((rightName: string) => {
          const controls: any = {};
          const fullName = lev1 + '.' + lev2 + '.' + rightName;
          this.roles.forEach((role: RoleModel) => {
            const value = role.rights?.includes(fullName);
            const control = new UntypedFormControl({value, disabled: true});
            controls[role.name] = control;
            this.form.addControl(role.name + '_' + fullName, control);
          });
          subGroup.rights.push({
            name: fullName,
            label: rightName,
            controls
          });
        });
        mainGroup.subGroups.push(subGroup);
      });
      this.rights.push(mainGroup);
    });
  }

  protected setRoles(roles: Array<any>): void {
    this.roles = roles.map((role: any) => {
      const result = new RoleModel().deserialize(role);
      result.setTranslocoService(this.transloco);
      return result;
    });
  }

}
